/**
 * Documents API client — Document Vault (Epic 4)
 */

const GATEWAY_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

export type DocumentType =
  | 'identity' | 'income' | 'property'
  | 'bank_statements' | 'tax_returns' | 'other'

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  identity: 'Identity',
  income: 'Income Proof',
  property: 'Property Contract',
  bank_statements: 'Bank Statements',
  tax_returns: 'Tax Returns',
  other: 'Other',
}

export interface CustomerDocument {
  id: string
  document_type: DocumentType
  file_name: string
  blob_name: string
  file_size: number
  mime_type: string
  shared_with_broker: boolean
  created_at: string | null
}

export interface DocumentListResponse {
  documents: CustomerDocument[]
  count: number
  storage_used: number
}

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}` }
}

export async function uploadDocument(
  token: string,
  file: File,
  documentType: DocumentType
): Promise<CustomerDocument> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('document_type', documentType)
  const res = await fetch(`${GATEWAY_BASE}/api/profile/documents`, {
    method: 'POST',
    headers: authHeaders(token),
    body: formData,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    const detail = err.detail
    const message = typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d: { msg?: string }) => d.msg || JSON.stringify(d)).join('; ')
        : `Upload failed: HTTP ${res.status}`
    throw new Error(message)
  }
  return res.json()
}

export async function listDocuments(token: string): Promise<DocumentListResponse> {
  const res = await fetch(`${GATEWAY_BASE}/api/profile/documents`, {
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(`Failed to load documents: HTTP ${res.status}`)
  return res.json()
}

export async function getDownloadUrl(
  token: string,
  docId: string
): Promise<{ url: string; expires_in_seconds: number }> {
  const res = await fetch(`${GATEWAY_BASE}/api/profile/documents/${docId}/download`, {
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(`Download failed: HTTP ${res.status}`)
  return res.json()
}

export async function deleteDocument(token: string, docId: string): Promise<void> {
  const res = await fetch(`${GATEWAY_BASE}/api/profile/documents/${docId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Delete failed: HTTP ${res.status}`)
  }
}

export async function shareDocument(
  token: string,
  docId: string,
  shared: boolean
): Promise<CustomerDocument> {
  const res = await fetch(`${GATEWAY_BASE}/api/profile/documents/${docId}/share`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ shared }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Share failed: HTTP ${res.status}`)
  }
  return res.json()
}

export async function getBrokerClientDocuments(
  token: string,
  customerId: string
): Promise<{ documents: CustomerDocument[]; count: number }> {
  const res = await fetch(`${GATEWAY_BASE}/api/broker/clients/${customerId}/documents`, {
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error(`Failed to load documents: HTTP ${res.status}`)
  return res.json()
}
